import React, { useCallback, useMemo } from 'react';

import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { Container, Stack } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';

import useAppNavigation from '~/hooks/useAppNavigation';
import useQuery from '~/hooks/useQuery';

import { addQuery, removeQuery } from '~/utils/url';

import BookingList from './BookingList';
import BookingSearchFilters from './BookingSearchFilters';
import { SearchFilters } from './constants';

const DEFAULT_SIZE_PER_PAGE = 10;

export default function BookingsPage() {
  const query = useQuery();
  const { push } = useAppNavigation();
  const location = useLocation();
  const brand = useSelector((state: App.State) => state.tenant.brand);

  const currentPage = useMemo(() => parseInt(query.get('page')) || 1, [query]);

  const searchFilters = useMemo(() => {
    const filters: SearchFilters = {};

    if (query.get('bookingNumber')) filters.bookingNumber = query.get('bookingNumber');
    if (query.get('sessionId')) filters.sessionId = query.get('sessionId');
    if (query.get('orderId')) filters.orderId = query.get('orderId');
    if (query.get('customerId')) filters.customerId = query.get('customerId');
    if (query.get('departureId')) filters.departureId = query.get('departureId');

    return filters;
  }, [query]);

  const onSearch = useCallback(
    (params: SearchFilters) => {
      const filtersToRemove: Array<string> = [];

      if (!params.bookingNumber) filtersToRemove.push('bookingNumber');
      if (!params.sessionId) filtersToRemove.push('sessionId');
      if (!params.orderId) filtersToRemove.push('orderId');
      if (!params.customerId) filtersToRemove.push('customerId');
      if (!params.departureId) filtersToRemove.push('departureId');

      const newLocation = addQuery(location, {
        bookingNumber: params.bookingNumber,
        sessionId: params.sessionId,
        orderId: params.orderId,
        customerId: params.customerId,
        departureId: params.departureId,
      });

      push(removeQuery(newLocation, ...filtersToRemove));
    },
    [push, location],
  );

  const onPageChange = (page) => {
    const newLocation = addQuery(location, { page });
    push(newLocation);
  };

  return (
    <Container maxWidth="xl">
      <PageHeader title="Booking Logs" />
      <Stack direction="column" spacing={2}>
        <BookingSearchFilters querySearchFilters={searchFilters} onSearch={onSearch} />

        <BookingList
          brand={brand}
          page={currentPage}
          currentPage={currentPage}
          onPageChange={onPageChange}
          searchFilters={searchFilters}
          sizePerPage={DEFAULT_SIZE_PER_PAGE}
        />
      </Stack>
    </Container>
  );
}
